import { useCallback, useRef, useEffect } from 'react';
import { useProgress } from './useProgress';

type SoundEffect = 'correct' | 'wrong' | 'splash' | 'squeak' | 'celebration' | 'levelUp' | 'achievement';

// Sound URLs - using base64 encoded short sounds for now
// In production, these would be actual audio files in /public/sounds/
const soundUrls: Record<SoundEffect, string> = {
  correct: '/sounds/correct.mp3',
  wrong: '/sounds/wrong.mp3',
  splash: '/sounds/splash.mp3',
  squeak: '/sounds/squeak.mp3',
  celebration: '/sounds/celebration.mp3',
  levelUp: '/sounds/levelup.mp3',
  achievement: '/sounds/achievement.mp3',
};

export function useSound() {
  const { progress } = useProgress();
  const audioRefs = useRef<Map<SoundEffect, HTMLAudioElement>>(new Map());

  // Pre-load sounds
  useEffect(() => {
    Object.entries(soundUrls).forEach(([key, url]) => {
      const audio = new Audio(url);
      audio.preload = 'auto';
      audio.volume = 0.5;
      audioRefs.current.set(key as SoundEffect, audio);
    });

    return () => {
      audioRefs.current.forEach((audio) => {
        audio.pause();
        audio.src = '';
      });
      audioRefs.current.clear();
    };
  }, []);

  const playSound = useCallback((sound: SoundEffect) => {
    if (!progress.settings.soundEnabled) {
      return;
    }

    const audio = audioRefs.current.get(sound);
    if (audio) {
      audio.currentTime = 0;
      audio.play().catch(() => {
        // Ignore autoplay errors
      });
    }
  }, [progress.settings.soundEnabled]);

  const playCorrect = useCallback(() => playSound('correct'), [playSound]);
  const playWrong = useCallback(() => playSound('wrong'), [playSound]);
  const playSplash = useCallback(() => playSound('splash'), [playSound]);
  const playSqueak = useCallback(() => playSound('squeak'), [playSound]);
  const playCelebration = useCallback(() => playSound('celebration'), [playSound]);
  const playLevelUp = useCallback(() => playSound('levelUp'), [playSound]);
  const playAchievement = useCallback(() => playSound('achievement'), [playSound]);

  return {
    playSound,
    playCorrect,
    playWrong,
    playSplash,
    playSqueak,
    playCelebration,
    playLevelUp,
    playAchievement,
  };
}
